import type { ActivityOutcomeKind } from "@common/schemas/domains/activity/activity-outcomes.schema";
import type { PickerSectionData } from "@design-system/Picker/Picker.types";
import {
    ACTIVITY_OUTCOME_LABELS,
    isActivityOutcomeKind,
} from "./activityTimelineOutcomes.lib";

const OUTCOME_VALUE_PREFIX = "outcome:";

/** An outcome's value in a filter dropdown, apart from its object options' values. */
export const toOutcomeValue = (outcome: ActivityOutcomeKind): string =>
    `${OUTCOME_VALUE_PREFIX}${outcome}`;

const parseOutcomeValue = (value: string): ActivityOutcomeKind | undefined => {
    const kind = value.slice(OUTCOME_VALUE_PREFIX.length);
    return value.startsWith(OUTCOME_VALUE_PREFIX) && isActivityOutcomeKind(kind)
        ? kind
        : undefined;
};

interface ResolveOutcomeSelectionParams {
    /** Every value the dropdown now holds, outcomes and objects alike. */
    values: readonly string[];
    outcome: ActivityOutcomeKind | undefined;
    selectedObjectValues: readonly string[];
}

interface OutcomeSelection {
    outcome: ActivityOutcomeKind | undefined;
    objectValues: string[];
}

/**
 * What a change in a dropdown with outcomes and objects leaves picked: a new
 * outcome replaces every other pick, and a new object drops the outcome. An
 * outcome already names its objects, so stacking them would repeat or
 * contradict it.
 */
export function resolveOutcomeSelection({
    values,
    outcome,
    selectedObjectValues,
}: ResolveOutcomeSelectionParams): OutcomeSelection {
    const outcomes = values.flatMap((value) => parseOutcomeValue(value) ?? []);
    const objectValues = values.filter(
        (value) => parseOutcomeValue(value) === undefined,
    );
    const pickedOutcome = outcomes.find((kind) => kind !== outcome);
    if (pickedOutcome) {
        return { outcome: pickedOutcome, objectValues: [] };
    }
    const hasNewObject = objectValues.some(
        (value) => !selectedObjectValues.includes(value),
    );
    return { outcome: hasNewObject ? undefined : outcomes[0], objectValues };
}

interface OutcomeFilterItem {
    value: string;
    label: string;
}

interface BuildOutcomeFilterOptionsParams {
    outcomes: readonly ActivityOutcomeKind[];
    outcome: ActivityOutcomeKind | undefined;
    /** The section listed under the outcomes, e.g. objects or actions. */
    objectSection: {
        key: string;
        title: string;
        items: readonly OutcomeFilterItem[];
    };
    selectedObjectValues: string[];
}

interface OutcomeFilterOptions {
    outcomeItems: OutcomeFilterItem[];
    /** A pick-one outcomes section above `objectSection`. */
    sections: Array<PickerSectionData<string>>;
    labelsByValue: Map<string, string>;
    /** The outcome's value when one is picked, otherwise the object picks. */
    selectedValues: string[];
}

/** What a dropdown with outcomes and objects lists, labels, and shows as picked. */
export function buildOutcomeFilterOptions({
    outcomes,
    outcome,
    objectSection,
    selectedObjectValues,
}: BuildOutcomeFilterOptionsParams): OutcomeFilterOptions {
    const outcomeItems = outcomes.map((kind) => ({
        value: toOutcomeValue(kind),
        label: ACTIVITY_OUTCOME_LABELS[kind],
    }));
    return {
        outcomeItems,
        sections: [
            {
                key: "outcomes",
                title: "Outcomes",
                exclusive: true,
                items: outcomeItems,
            },
            objectSection,
        ],
        labelsByValue: new Map(
            [...outcomeItems, ...objectSection.items].map((item) => [
                item.value,
                item.label,
            ]),
        ),
        selectedValues: outcome
            ? [toOutcomeValue(outcome)]
            : selectedObjectValues,
    };
}
